import React, { createContext, useContext, useEffect, useState } from 'react';
import { uniqBy } from 'lodash';
import { useApolloClient } from '../../hooks/useApolloClient';
import queryFeed from '../../queries/getFeed';
import listSavedSearchs from '../../queries/listSavedSearches';
import findSavedSearchByData from '../../queries/findSavedSearchByData';
import FiltersColumn from './components/FiltersColumn/FiltersColumn';
import css from './Feed.module.css';
import FeedList from './components/FeedList/FeedList';
import FeedHeader from './components/FeedHeader/FeedHeader';
import SidebarHeader from './components/SidebarHeader/SidebarHeader';
import ToggleSideNav from './components/ToggleSidenav/ToggleSidenav';
import useNavigation from '../../v2/hooks/useNavigation';
import { useSharedState } from '../../contexts/SharedStateContext';

const UPDATED_DAYS = 30;

const resultTypeNormalize = (type) => {
  switch (type) {
    case 'events':
      return 'event';
    case 'regulations':
      return 'regulation';
    default:
      return type;
  }
};

const fetchFeed = ({
  client,
  topics = [],
  entityIds = [],
  industryIds = [],
  updatedInLastDays = UPDATED_DAYS,
  filter,
  includeBookmarks = false,
  includeConnections = false,
  includeNotes = false,
  includeCompliance = false,
  limit = 100,
  offset = 0,
}) => {
  return client
    .query({
      query: queryFeed,
      variables: {
        topics,
        entityIds,
        industryIds,
        updatedInLastDays,
        filter: filter.map(resultTypeNormalize), // TODO: Fix when moving to v2 resolvers
        includeBookmarks,
        includeConnections,
        includeCompliance,
        includeNotes,
        limit,
        offset,
      },
    })
    .then(({ data }) => data.getFeed);
};

const FeedContext = createContext();

const Feed = () => {
  const client = useApolloClient();

  /*
   * These are our current feed filters
   * "feedEntity" is the entity we are filtering by
   * "feedCountries" is an array of { id, name } object for countries
   * "feedIndustries" is an array of { id, name } objects for briefs
   * "feedResultType" is the type of data we are asking for (Events, Roles, or All (Roles + Regulations))
   */
  const [feedEntities, setFeedEntities] = useState([]);
  const [feedResultType, setFeedResultType] = useState('all');
  const [feedIndustries, setFeedIndustries] = useState([]);
  const [feedCountries, setFeedCountries] = useState([]);
  const [searchDates, setSearchDates] = useState([]);
  const [savedSearches, setSavedSearches] = useState([]);
  const [extraFilter, setExtraFilter] = useState('');
  const [savedSearchName, setSavedSearchName] = useState(null);
  const [savedSearchId, setSavedSearchId] = useState(null);
  const [homeFeedSavedSearch, setHomeFeedSavedSearch] = useState(null);
  const [refreshSavedSearches, setRefreshSavedSearches] = useState(false);
  const [loadFeed, setLoadFeed] = useState(false);
  const { params, pushParam, deleteParam, replaceParams } = useNavigation();
  const { selectedSavedSearch, setSelectedSavedSearch } = useSharedState();

  const [pagination, setPagination] = useState({ limit: 500, offset: 0 });

  const [results, setResults] = useState([]);
  const [totals, setTotals] = useState([]);
  const [isResultsLoading, setIsResultsLoading] = useState(false);

  const isFilterOpen = params.get('isFilterOpen');

  const fetchFeedResults = ({
    entityIds = [],
    resultType,
    countries,
    industries,
    pageInfo,
  }) => {
    let filter = ['role', 'education', 'event', 'regulation'];

    if (resultType !== 'all' && !resultType.includes('all')) {
      if (Array.isArray(resultType)) {
        filter = resultType;
      } else {
        filter = [resultType];
      }
    }

    fetchFeed({
      client,
      topics: countries.length === 0 ? [] : countries,
      entityIds,
      industryIds: industries,
      filter,
      includeBookmarks: extraFilter === 'bookmarks',
      includeConnections: extraFilter === 'connections',
      includeNotes: extraFilter === 'notes',
      includeCompliance: extraFilter === 'compliant',
      limit: pageInfo.limit,
      offset: pageInfo.offset,
    })
      .then((data) => {
        const { total, updates } = data;
        setTotals(total);
        setResults((currentResults) =>
          uniqBy([...currentResults, ...updates], 'id'),
        );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIsResultsLoading(false);
      });
  };

  const findSavedSearch = async () => {
    const searchData = {
      filterEntities: feedEntities.map((entity) => entity.id),
      filterIndustries: feedIndustries.map((industry) => industry.id),
      name: savedSearchName || '',
      topics: feedCountries.map((country) => country.id),
      filterCategories:
        feedResultType && feedResultType !== 'all' ? [feedResultType] : [],
      searchDates,
      isHomeFeed: false,
    };

    const [{ data }, { data: homeFeedData }] = await Promise.all([
      client.query({
        query: findSavedSearchByData,
        variables: { searchData: JSON.stringify(searchData) },
        fetchPolicy: 'network-only',
      }),
      client.query({
        query: findSavedSearchByData,
        variables: {
          searchData: JSON.stringify({ ...searchData, isHomeFeed: true }),
        },
        fetchPolicy: 'network-only',
      }),
    ]);

    if (data.listSavedSearchs.savedSearches.length > 0) {
      setSavedSearchName(data.listSavedSearchs.savedSearches[0].name);
      setSavedSearchId(data.listSavedSearchs.savedSearches[0].id);
    }

    const homeFeedSavedSearchItem =
      homeFeedData.listSavedSearchs.savedSearches.find(
        (d) => JSON.parse(d.search_data).isHomeFeed,
      );

    if (homeFeedSavedSearchItem) {
      setHomeFeedSavedSearch(JSON.parse(homeFeedSavedSearchItem.search_data));
      setSavedSearchName(homeFeedSavedSearchItem.name);
      setSavedSearchId(homeFeedSavedSearchItem.id);
    }
  };

  useEffect(() => {
    client
      .query({
        query: listSavedSearchs,
        variables: { feedEnabled: true },
        fetchPolicy: 'network-only',
      })
      .then(({ data }) => {
        setSavedSearches(data.listSavedSearchs.savedSearches);
        const homeFeedSearch = data.listSavedSearchs.savedSearches.find(
          (s) => JSON.parse(s.search_data).isHomeFeed,
        );

        if (homeFeedSearch) {
          const searchData = JSON.parse(homeFeedSearch.search_data);
          setSavedSearchName(homeFeedSearch.name);
          setHomeFeedSavedSearch(searchData);
          setFeedCountries(searchData.topics);
          setFeedIndustries(searchData.filterIndustries);
          setFeedEntities(searchData.filterEntities);
          setSearchDates(searchData.searchDates);
          setFeedResultType(searchData.filterCategories[0] || feedResultType);
          setSavedSearchId(homeFeedSearch.id);
          setSelectedSavedSearch(homeFeedSearch);
        } else {
          setHomeFeedSavedSearch(null);
          setSelectedSavedSearch(null);
        }
        setLoadFeed(true);
      });
  }, [refreshSavedSearches]);

  useEffect(() => {
    if (!loadFeed) return;

    setResults([]);
    setIsResultsLoading(true);
    setPagination({ limit: 100, offset: 0 });

    findSavedSearch().then(() => {
      fetchFeedResults({
        entityIds: homeFeedSavedSearch
          ? homeFeedSavedSearch.filterEntities.map((entity) => entity.id)
          : feedEntities.map((entity) => entity.id),
        resultType: feedResultType,
        countries: homeFeedSavedSearch
          ? homeFeedSavedSearch.topics.map((country) => country.id)
          : feedCountries.map((country) => country.id),
        industries: homeFeedSavedSearch
          ? homeFeedSavedSearch.filterIndustries.map((industry) => industry.id)
          : feedIndustries.map((industry) => industry.id),
        pageInfo: { limit: 100, offset: 0 },
      });
    });
  }, [
    feedEntities.length,
    feedResultType,
    feedIndustries,
    feedCountries,
    extraFilter,
    loadFeed,
  ]);

  useEffect(() => {
    if (pagination.offset === 0) return;
    setIsResultsLoading(true);
    fetchFeedResults({
      entityIds: feedEntities.map((entity) => entity.id),
      resultType: feedResultType,
      countries: feedCountries.map((country) => country.id),
      industries: feedIndustries.map((industry) => industry.id),
      pageInfo: pagination,
    });
  }, [pagination]);

  const applySavedSearchFilter = (savedSearch) => {
    setSavedSearchName(savedSearch.name);
    setSavedSearchId(savedSearch.id);

    const searchData = JSON.parse(savedSearch.search_data);

    setFeedEntities(searchData.filterEntities);
    setFeedCountries(searchData.topics);
    setFeedIndustries(searchData.filterIndustries);
    setFeedResultType(searchData.filterCategories[0] || feedResultType);
    setHomeFeedSavedSearch(savedSearch.isHomeFeed ? savedSearch : null);
  };

  /**
   * This effect runs when user select a saved search on leftPanel
   */
  useEffect(() => {
    if (!selectedSavedSearch || selectedSavedSearch === savedSearchId) return;

    applySavedSearchFilter(selectedSavedSearch);
  }, [selectedSavedSearch]);

  // Remove saved search when unMounting component
  useEffect(() => () => setSelectedSavedSearch(null), []);

  const clearSavedSearchFilter = (type) => {
    setSavedSearchName(null);
    setHomeFeedSavedSearch(null);
    setSavedSearchId(null);

    switch (type) {
      case 'country':
        setFeedCountries([]);
        break;
      case 'industry':
        setFeedIndustries([]);
        break;
      case 'entity':
        setFeedEntities([]);
        break;
      case 'type':
        setFeedResultType('all');
        break;
      default: {
        setFeedCountries([]);
        setFeedIndustries([]);
        setFeedEntities([]);
        setSearchDates([]);
      }
    }
  };

  const onFilterButtonClicked = () => {
    if (params.get('leftPanel')) {
      params.delete('leftPanel');
      params.set('isFilterOpen', true);
      replaceParams(params);
    } else {
      pushParam('isFilterOpen', true);
    }
  };

  const filterStatus = isFilterOpen ? 'expanded' : 'collapsed';

  return (
    <FeedContext.Provider
      value={{
        feedEntities,
        feedResultType,
        feedIndustries,
        feedCountries,
        pagination,
        results,
        totals,
        isResultsLoading,
        extraFilter,
        savedSearches,
        savedSearchName,
        refreshSavedSearches,
        homeFeedSavedSearch,
        savedSearchId,
        searchDates,

        setSavedSearches,
        setSavedSearchName,
        setFeedEntities,
        setFeedResultType,
        setFeedIndustries,
        setFeedCountries,
        setPagination,
        setExtraFilter,
        setHomeFeedSavedSearch,
        setRefreshSavedSearches,
        setSavedSearchId,
        setSearchDates,

        clearSavedSearchFilter,
      }}
    >
      <div data-cy="feed" className={css.root}>
        <div className={css.container}>
          <ToggleSideNav
            filterStatus={filterStatus}
            onClick={onFilterButtonClicked}
          />
          <div
            data-cy="feed-filters"
            data-status={filterStatus}
            className={css.sidebar}
          >
            <SidebarHeader onClose={() => deleteParam('isFilterOpen')} />
            <FiltersColumn />
          </div>
          <div
            data-cy="feed-events"
            data-status={filterStatus}
            className={css.content}
          >
            <FeedHeader />
            <FeedList updatedDays={UPDATED_DAYS} />
          </div>
        </div>
      </div>
    </FeedContext.Provider>
  );
};

export default Feed;

export const useFeed = () => useContext(FeedContext);
